import React from "react";
import SiteLayout from "../../components/layout/SiteLayout";
import PageIntro from "../../components/ui/PageIntro";
import LoadingOverlay from "../../components/feedback/LoadingOverlay";
import { getMerchCatalog } from "../../services/api/http";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBoxOpen,
  FaStar,
  FaTshirt,
} from "react-icons/fa";
import "./MerchPage.css";

const formatPrice = (price, currency) => {
  if (price == null) {
    return "Price unavailable";
  }

  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD"
  }).format(price / 100);
};

class ProductSlideshow extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      activeIndex: 0,
      aspectRatio: null,
      isImageLoading: true
    };
  }

  componentDidUpdate(prevProps) {
    if (prevProps.images !== this.props.images) {
      this.setState({
        activeIndex: 0,
        aspectRatio: null,
        isImageLoading: true
      });
    }
  }

  setActiveIndex = (activeIndex) => {
    this.setState({
      activeIndex,
      aspectRatio: null,
      isImageLoading: true
    });
  };

  goPrevious = (event) => {
    event.preventDefault();
    const { images } = this.props;
    this.setState((currentState) => ({
      activeIndex:
        currentState.activeIndex === 0
          ? images.length - 1
          : currentState.activeIndex - 1,
      aspectRatio: null,
      isImageLoading: true
    }));
  };

  goNext = (event) => {
    event.preventDefault();
    const { images } = this.props;
    this.setState((currentState) => ({
      activeIndex:
        currentState.activeIndex === images.length - 1
          ? 0
          : currentState.activeIndex + 1,
      aspectRatio: null,
      isImageLoading: true
    }));
  };

  handleImageLoad = (event) => {
    const { naturalWidth, naturalHeight } = event.target;
    if (!naturalWidth || !naturalHeight) {
      return;
    }

    this.setState({
      aspectRatio: `${naturalWidth} / ${naturalHeight}`,
      isImageLoading: false
    });
  };

  render() {
    const { images, name } = this.props;
    const { activeIndex, aspectRatio, isImageLoading } = this.state;

    if (!images || images.length === 0) {
      return (
        <div className="merch-slideshow-frame merch-card-image-fallback">
          {name}
        </div>
      );
    }

    return (
      <div
        className="merch-slideshow-frame"
        style={aspectRatio ? { aspectRatio } : undefined}
      >
        {isImageLoading ? (
          <div className="merch-slide-loading" aria-hidden="true">
            <div className="spinner-dot" />
            <div className="spinner-dot" />
            <div className="spinner-dot" />
          </div>
        ) : null}
        <img
          key={`${name}-${activeIndex}-${images[activeIndex]}`}
          className={`merch-card-image ${isImageLoading ? "is-loading" : ""}`}
          src={images[activeIndex]}
          alt={`${name} preview ${activeIndex + 1}`}
          onLoad={this.handleImageLoad}
        />
        {images.length > 1 ? (
          <>
            <button
              type="button"
              className="merch-slide-button merch-slide-button-prev"
              onClick={this.goPrevious}
              aria-label={`Show previous ${name} image`}
            >
              <FaArrowLeft />
            </button>
            <button
              type="button"
              className="merch-slide-button merch-slide-button-next"
              onClick={this.goNext}
              aria-label={`Show next ${name} image`}
            >
              <FaArrowRight />
            </button>
            <div className="merch-slide-dots">
              {images.map((imageUrl, index) => (
                <button
                  key={`${imageUrl}-${index}`}
                  type="button"
                  className={`merch-slide-dot ${
                    index === activeIndex ? "is-active" : ""
                  }`}
                  onClick={(event) => {
                    event.preventDefault();
                    this.setActiveIndex(index);
                  }}
                  aria-label={`Show ${name} image ${index + 1}`}
                />
              ))}
            </div>
          </>
        ) : null}
      </div>
    );
  }
}

export default class MerchPage extends React.Component {
  constructor() {
    super();
    this.state = {
      loading: true,
      merchItems: [],
      error: null
    };
  }

  componentDidMount() {
    getMerchCatalog()
      .then((response) => {
        this.setState({
          merchItems: response.data || [],
          loading: false
        });
      })
      .catch((error) => {
        console.error(error);
        const message =
          error?.response?.data?.message ||
          error?.response?.data?.error ||
          error?.message ||
          "Unable to load merch right now.";
        this.setState({
          loading: false,
          error: message
        });
      });
  }

  renderBuyAction(item) {
    if (!item.buyUrl) {
      return (
        <div className="meta-copy">
          I do not have a live buy link for this item yet. I need to publish
          the product externally to enable checkout here.
        </div>
      );
    }

    return (
      <a
        href={item.buyUrl}
        target="_blank"
        rel="noreferrer"
        className="button-primary merch-buy-button"
      >
        Buy with Printify <FaArrowRight />
      </a>
    );
  }

  render() {
    const { loading, merchItems, error } = this.state;
    const featuredItem = merchItems[0] || null;
    const totalProducts = merchItems.length;
    const availableCount = merchItems.filter((item) => item.buyUrl).length;

    return (
      <SiteLayout>
        <LoadingOverlay show={loading} />
        <main className="page-shell merch-page home-v3-shell">
          <PageIntro
            eyebrow="Merch / Gorilla Athletics"
            title="Training gear with a cleaner point of view."
            description="Premium apparel and everyday pieces, kept current through Printify with previews, prices, and purchase links in one place."
            actions={[
              { label: "Shop the catalog", href: "#catalog", variant: "primary" },
            ]}
            visual={(
              <div className="merch-hero-panel home-v3-panel card-pad">
                <span className="home-v3-kicker">Storefront</span>
                <h2>What shoppers get.</h2>
                <div className="merch-hero-highlights">
                  <div className="merch-hero-highlight">
                    <FaBoxOpen />
                    <div><strong>Current lineup</strong><p>{totalProducts} products live in the catalog.</p></div>
                  </div>
                  <div className="merch-hero-highlight">
                    <FaTshirt />
                    <div><strong>Wearable first</strong><p>Clean, simple pieces for training and casual wear.</p></div>
                  </div>
                  <div className="merch-hero-highlight">
                    <FaStar />
                    <div><strong>Direct from Printify</strong><p>Previews, pricing, and checkout links stay current.</p></div>
                  </div>
                </div>
              </div>
            )}
          />

          <section className="section merch-catalog-section" id="catalog">
            <div className="merch-section-head">
              <div>
                <span className="home-v3-kicker">Catalog</span>
                <h2>Products I keep available.</h2>
                <p>
                  Each product card shows the current preview, pricing, and buy
                  link when checkout is available.
                </p>
              </div>
              <div className="merch-section-meta">
                <span className="pill">
                  <FaBoxOpen /> {totalProducts} products
                </span>
                <span className="pill">
                  <FaArrowRight /> {availableCount} buy links
                </span>
              </div>
            </div>

            {error ? <div className="home-v3-panel card-pad">{error}</div> : null}

            {!error && merchItems.length === 0 && !loading ? (
              <div className="home-v3-panel card-pad merch-empty-state">
                <FaTshirt />
                No merch is currently available.
              </div>
            ) : null}

            {featuredItem ? (
              <article className="home-v3-panel merch-featured">
                <div className="merch-featured-media">
                  <ProductSlideshow
                    images={
                      featuredItem.imageUrls?.length
                        ? featuredItem.imageUrls
                        : featuredItem.imageUrl
                          ? [featuredItem.imageUrl]
                          : []
                    }
                    name={featuredItem.name}
                  />
                </div>
                <div className="merch-featured-content card-pad">
                  <span className="app-badge">Featured Drop</span>
                  <h3 className="feature-title merch-featured-title">
                    {featuredItem.name}
                  </h3>
                  <p className="feature-copy merch-featured-copy">
                    {featuredItem.description ||
                      "A current highlight from the Gorilla Athletics catalog."}
                  </p>
                  <div className="merch-featured-meta">
                    <span className="pill merch-card-price">
                      {formatPrice(featuredItem.price, featuredItem.currency)}
                    </span>
                    <span className="pill merch-featured-note">
                      Best seller spotlight
                    </span>
                  </div>
                  <div className="hero-actions merch-featured-actions">
                    {this.renderBuyAction(featuredItem)}
                  </div>
                </div>
              </article>
            ) : null}

            <div className="card-grid">
              {merchItems.slice(1).map((item) => (
                <article className="home-v3-panel merch-card" key={item.id || item.name}>
                  <div className="merch-card-media">
                    <ProductSlideshow
                      images={
                        item.imageUrls?.length
                          ? item.imageUrls
                          : item.imageUrl
                            ? [item.imageUrl]
                            : []
                      }
                      name={item.name}
                    />
                  </div>
                  <div className="card-pad">
                    <div className="merch-card-header">
                      <div>
                        <span className="app-badge merch-card-badge">Printify Product</span>
                        <h3 className="feature-title merch-card-title">
                          {item.name}
                        </h3>
                      </div>
                      <span className="pill merch-card-price">
                        {formatPrice(item.price, item.currency)}
                      </span>
                    </div>
                    <p className="feature-copy">
                      {item.description || "I list this item from my Printify catalog."}
                    </p>
                    <div className="merch-card-footer">
                      <div className="merch-card-copy">
                        <span className="merch-card-label">Why it stands out</span>
                        <p className="meta-copy">
                          Sleek branding, direct fulfillment, and a clean store
                          presentation that makes the product easy to scan.
                        </p>
                      </div>
                      <div className="merch-card-actions">
                        {this.renderBuyAction(item)}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>
        </main>
      </SiteLayout>
    );
  }
}
